/* eslint-disable no-undef */
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { getAccessToken } from 'utils/authClient';

async function onRequestWithToken(config: AxiosRequestConfig): Promise<AxiosRequestConfig> {
  const token = await getAccessToken();
  if (!token) {
    return config;
  }
  config.headers['Authorization'] = `Bearer ${token}`;
  return config;
}

function onRequest(config: AxiosRequestConfig): AxiosRequestConfig {
  return config;
}

function onResponseErrorWithRetry(error: AxiosError): Promise<never> {
  //@ts-ignore
  if (error.response?.status === 401 && !error.config?.isRetry) {
    return getAccessToken(true).then(token => {
      error.config.headers['Authorization'] = `Bearer ${token}`;
      //@ts-ignore
      error.config.isRetry = true;
      return axios.request(error.config);
    });
  }
  return Promise.reject(error?.response);
}

function onResponseError(error: AxiosError): Promise<never> {
  return Promise.reject(error?.response);
}

const portalInstance = axios.create({
  baseURL: process.env.REACT_APP_PORTAL_API_URL,
});
portalInstance.interceptors.request.use(onRequestWithToken);
portalInstance.interceptors.response.use(undefined, onResponseErrorWithRetry);

const mychooosePortalInstance = axios.create({
  baseURL: process.env.REACT_APP_PORTAL_API_URL,
  headers: {
    'x-functions-key': process.env.REACT_APP_MYCHOOOSE_API_KEY,
  },
});
mychooosePortalInstance.interceptors.request.use(onRequestWithToken);
mychooosePortalInstance.interceptors.response.use(undefined, onResponseErrorWithRetry);

const wechooosePortalInstance = axios.create({
  baseURL: process.env.REACT_APP_PORTAL_API_URL,
  headers: {
    'x-functions-key': process.env.REACT_APP_WECHOOOSE_API_KEY,
  },
});
wechooosePortalInstance.interceptors.request.use(onRequestWithToken);
wechooosePortalInstance.interceptors.response.use(undefined, onResponseErrorWithRetry);

const anonymousPortalInstance = axios.create({
  baseURL: process.env.REACT_APP_PORTAL_API_URL,
});
anonymousPortalInstance.interceptors.request.use(onRequest);
anonymousPortalInstance.interceptors.response.use(undefined, onResponseError);

const publicInstance = axios.create({
  baseURL: process.env.REACT_APP_PUBLIC_API_URL,
  headers: {
    'x-functions-key': process.env.REACT_APP_PUBLIC_API_FUNCTION_KEY,
  },
});
publicInstance.interceptors.request.use(onRequest);
publicInstance.interceptors.response.use(undefined, onResponseError);

const sanityInstance = axios.create({
  baseURL: `https://${process.env.REACT_APP_SANITY_PROJECT_ID}.api.sanity.io/v1/data/query/${process.env.REACT_APP_SANITY_DATASET}`,
  headers: {
    Authorization: `Bearer ${process.env.REACT_APP_SANITY_TOKEN}`,
  },
});
sanityInstance.interceptors.request.use(onRequest);
sanityInstance.interceptors.response.use(undefined, onResponseError);

function getInstance(context: PortalContext): AxiosInstance {
  let instance = anonymousPortalInstance;
  if (['guest-wechooose', 'wechooose', 'connect'].includes(context)) {
    instance = wechooosePortalInstance;
  } else if (['guest-mychooose', 'mychooose'].includes(context)) {
    instance = mychooosePortalInstance;
  }

  return instance;
}

type InjectableFunction<TFunction> = (instance: AxiosInstance) => TFunction;

function createContextAwareHandlers<T extends { [x: string]: InjectableFunction<ReturnType<T[K]>> }, K extends keyof T>(
  context: PortalContext,
  handlers: T,
): { [K in keyof T]: ReturnType<T[K]> } {
  const instance = getInstance(context);
  const injectedHandlers: { [K in keyof T]: ReturnType<T[K]> } = Object.assign(
    {},
    ...Object.entries(handlers).map(([key, handler]) => ({ [key]: handler(instance) })),
  );

  return injectedHandlers;
}

export {
  portalInstance,
  anonymousPortalInstance,
  publicInstance,
  sanityInstance,
  wechooosePortalInstance,
  mychooosePortalInstance,
  createContextAwareHandlers,
};
